import { useCallback, useEffect, useState } from "react";
import { useLocation, useParams } from "react-router-dom";
import { useSelector } from "react-redux";

import { Box, Button, Group, Modal, Tabs, Text } from "@mantine/core";
import { useDisclosure } from "@mantine/hooks";
import { useTranslation } from "react-i18next";

import { Container, LayoutStandard } from "components";
import { IRootState } from "config/redux";
import { AppConstants, AuthorityConstants, ServiceConstants } from "constant";
import { useServiceSuccess } from "hooks";
import { AbsenceService, UserService } from "services";
import { displayClasses } from "styles";
import { AppUtils, StorageUtils, UserUtils } from "utils";
import { UserResponse } from "types";

import AbsenceRequestModal from "./AbsenceRequestModal";
import AbsencesTab, { AbsenceResponse, HolidayResponse } from "./AbsencesTab";
import DocumentationTab from "./DocumentationTab";
import ProfileHeader from "./ProfileHeader";

const { STORAGE_USER } = AppConstants;
const {
  REQUEST_ABSENCE_LIST_ID,
  REQUEST_USERS_LIST_ID,
  REQUEST_ABSENCE_APPROVE_ID,
  REQUEST_ABSENCE_REJECT_ID,
  REQUEST_HOLIDAY_LIST_ID,
  REQUEST_PENDING,
} = ServiceConstants;

// TODO: Lični podaci (desna strana) zahtevaju poseban API
const mockPersonalData = {
  personalName: "Milica Lalić Rkulović",
  privatePhone: "+41 62 842 6000",
  privateEmail: "milica@gmail.com",
  startDate: "10.10.2015.",
  birthDate: "10.10.1985.",
  location: "Zaječar",
  contractType: "Na neodređeno",
  contactPerson: "Majka: +41 79636274",
  languages: ["Srpski", "Nemački"],
};

const CENTRAL_PHONE = "+41 62 842 6000";

const ProfilePage = () => {
  // test com,it
  const { t } = useTranslation();
  const { id } = useParams<{ id: string }>();
  const location = useLocation();
  const [modalOpened, { open: openModal, close: closeModal }] = useDisclosure(false);
  const [absences, setAbsences] = useState<AbsenceResponse[]>([]);
  const [holidays, setHolidays] = useState<HolidayResponse[]>([]);
  const [selectedAbsenceId, setSelectedAbsenceId] = useState<number | null>(null);
  const [loggedUser] = useState<UserResponse | undefined>(StorageUtils.getObject(STORAGE_USER));
  const [fetchedUser, setFetchedUser] = useState<UserResponse | undefined>(undefined);

  // Check if user can view other profiles (only admin with ROLE_USR_AGT)
  const canViewOtherProfiles = UserUtils.hasAuthority(AuthorityConstants.ROLE_USR_AGT);
  
  // Permission checks for absence features
  const canRequestAbsence = UserUtils.hasAuthority(AuthorityConstants.ROLE_ABS);
  const canApproveAbsence = UserUtils.hasAuthority(AuthorityConstants.ROLE_ABS_CLOSE);

  // Get user from navigation state (when admin views agent profile)
  const stateUser = (location.state as { user?: UserResponse })?.user;

  // Only allow viewing other profile if user has permission
  const isViewingOtherProfile = !!id && canViewOtherProfiles;

  // Permission check for documentation tab
  const canViewDocumentation = isViewingOtherProfile
    ? UserUtils.hasAuthority(AuthorityConstants.ROLE_USR_ATT)
    : UserUtils.hasAuthority(AuthorityConstants.ROLE_USR_ATT_MY);

  // Determine which user data to display
  const user = isViewingOtherProfile ? stateUser || fetchedUser : loggedUser;

  // Fetch user data if viewing other profile and no state available
  const usersServiceObject = useSelector(
    (state: IRootState) => state.service.object[REQUEST_USERS_LIST_ID],
  );

  useEffect(() => {
    if (isViewingOtherProfile && !stateUser && id) {
      // Fetch users list to find the user by ID
      UserService.usersAll({
        page: 0,
        search: "",
        profileId: 2,
        status: "ACTIVE",
        type: "EMPLOYEE",
      });
    }
  }, [isViewingOtherProfile, stateUser, id]);

  useEffect(() => {
    if (
      usersServiceObject?.status === "SUCCESS" &&
      usersServiceObject?.data?.content &&
      id &&
      !stateUser
    ) {
      const foundUser = usersServiceObject.data.content.find(
        (u: UserResponse) => u.id === Number(id),
      );
      if (foundUser) {
        setFetchedUser(foundUser);
      }
    }
  }, [usersServiceObject, id, stateUser]);

  const handleNewRequest = useCallback(() => {
    setSelectedAbsenceId(null);
    openModal();
  }, [openModal]);

  const handleAbsenceClick = useCallback(
    (id: number) => {
      setSelectedAbsenceId(id);
      openModal();
    },
    [openModal],
  );

  const handleModalClose = useCallback(() => {
    setSelectedAbsenceId(null);
    closeModal();
  }, [closeModal]);

  const [statusModalOpened, setStatusModalOpened] = useState(false);
  const [selectedAbsenceForStatus, setSelectedAbsenceForStatus] = useState<AbsenceResponse | null>(
    null,
  );

  const handleStatusEditClick = useCallback((absence: AbsenceResponse) => {
    setSelectedAbsenceForStatus(absence);
    setStatusModalOpened(true);
  }, []);

  const handleStatusModalClose = useCallback(() => {
    setStatusModalOpened(false);
    setSelectedAbsenceForStatus(null);
  }, []);

  const handleApprove = useCallback(() => {
    if (selectedAbsenceForStatus) {
      AbsenceService.absenceApprove(selectedAbsenceForStatus.id);
    }
  }, [selectedAbsenceForStatus]);

  const handleReject = useCallback(() => {
    if (selectedAbsenceForStatus) {
      AbsenceService.absenceReject(selectedAbsenceForStatus.id);
    }
  }, [selectedAbsenceForStatus]);

  // Build profile data from user + mock personal data
  const profileData = {
    // Poslovni podaci (dinamički iz korisnika)
    workName: user?.fullName || "",
    workRole: user?.profile?.name || "",
    workId: String(user?.id || ""),
    workEmail: user?.email || "",
    workPhone:
      user?.countryCode && user?.phoneNumber ? `${user.countryCode} ${user.phoneNumber}` : "",
    centralPhone: CENTRAL_PHONE,
    profileImage: user?.profileImage || undefined,

    // Lični podaci (mock - zahteva poseban API)
    ...mockPersonalData,
  };

  const serviceObject = useSelector(
    (state: IRootState) => state.service.object[REQUEST_ABSENCE_LIST_ID],
  );
  const isLoading = serviceObject?.status === REQUEST_PENDING;

  const fetchAbsences = useCallback(() => {
    if (isViewingOtherProfile && id) {
      AbsenceService.absenceList({ agentId: Number(id), pageSize: 100 });
    } else {
      AbsenceService.absenceList({ myAbsence: true, pageSize: 100 });
    }
  }, [isViewingOtherProfile, id]);

  // Status action success handler
  const handleStatusSuccess = useCallback(() => {
    handleStatusModalClose();
    fetchAbsences();
  }, [handleStatusModalClose, fetchAbsences]);

  // Track approve/reject success using custom hook
  const { isLoading: isApproveLoading } = useServiceSuccess(
    selectedAbsenceForStatus ? REQUEST_ABSENCE_APPROVE_ID + selectedAbsenceForStatus.id : undefined,
    { onSuccess: handleStatusSuccess },
  );
  const { isLoading: isRejectLoading } = useServiceSuccess(
    selectedAbsenceForStatus ? REQUEST_ABSENCE_REJECT_ID + selectedAbsenceForStatus.id : undefined,
    { onSuccess: handleStatusSuccess },
  );

  const isStatusActionLoading = isApproveLoading || isRejectLoading;

  useEffect(() => {
    setAbsences([]); // Reset absences when route changes
    fetchAbsences();
  }, [fetchAbsences]);

  useEffect(() => {
    if (serviceObject?.status === "SUCCESS") {
      setAbsences(serviceObject?.data?.content || []);
    }
  }, [serviceObject]);

  // Fetch holidays
  const holidayServiceObject = useSelector(
    (state: IRootState) => state.service.object[REQUEST_HOLIDAY_LIST_ID],
  );

  useEffect(() => {
    const currentYear = new Date().getFullYear();
    AbsenceService.holidayList({
      dateFrom: `${currentYear}-01-01`,
      dateTo: `${currentYear}-12-31`,
    });
  }, []);

  useEffect(() => {
    if (holidayServiceObject?.status === "SUCCESS" && Array.isArray(holidayServiceObject.data)) {
      setHolidays(holidayServiceObject.data);
    }
  }, [holidayServiceObject]);

  return (
    <LayoutStandard title={AppUtils.composeTabTitle(t("profile"))}>
      <Container>
        <Box px="xl" pt="xl">
          <ProfileHeader data={profileData} />

          {/* Tabovi */}
          <Tabs defaultValue="absences" mt={50} variant="unstyled">
            <Tabs.List>
              <Tabs.Tab value="absences" className={displayClasses.tabList}>
                {t("profilePage.tabs.absences")}
              </Tabs.Tab>
              <Tabs.Tab value="kpi" className={displayClasses.tabList}>
                KPI
              </Tabs.Tab>
              {canViewDocumentation && (
                <Tabs.Tab value="documentation" className={displayClasses.tabList}>
                  {t("profilePage.tabs.documentation")}
                </Tabs.Tab>
              )}
            </Tabs.List>

            <Tabs.Panel value="absences" className={displayClasses.tabContent}>
              <AbsencesTab
                absences={absences}
                holidays={holidays}
                onRequestClick={handleNewRequest}
                onAbsenceClick={handleAbsenceClick}
                onStatusEditClick={canApproveAbsence ? handleStatusEditClick : undefined}
                loading={isLoading}
                readOnly={!canRequestAbsence || isViewingOtherProfile}
                isAdmin={canApproveAbsence}
              />
            </Tabs.Panel>

            <Tabs.Panel value="kpi" className={displayClasses.tabContent}>
              <Text c="dimmed">Sadržaj za KPI...</Text>
            </Tabs.Panel>

            {canViewDocumentation && (
              <Tabs.Panel value="documentation" pt="xl">
                <DocumentationTab user={user} />
              </Tabs.Panel>
            )}
          </Tabs>
        </Box>
      </Container>

      <AbsenceRequestModal
        opened={modalOpened}
        onClose={handleModalClose}
        onSuccess={fetchAbsences}
        absenceId={selectedAbsenceId}
        holidays={holidays}
      />

      {/* Status Change Modal (Admin approve/reject) */}
      <Modal
        opened={statusModalOpened}
        onClose={handleStatusModalClose}
        title={t("profilePage.absences.statusModal.title")}
        centered
        size="sm"
        padding="lg"
        styles={{ title: { fontSize: 18, fontWeight: 600 } }}
      >
        <Text size="sm" mb="xl">
          {t("profilePage.absences.statusModal.description")}
        </Text>

        {selectedAbsenceForStatus?.note && (
          <Box mb="xl" ta="center">
            <Text size="sm" c="primary" fw={500}>
              {t("profilePage.absences.statusModal.agentNote")}
            </Text>
            <Text size="sm">{selectedAbsenceForStatus.note}</Text>
          </Box>
        )}

        <Group justify="center" gap="md">
          <Button
            color="red"
            radius="md"
            onClick={handleReject}
            loading={isRejectLoading}
            disabled={isStatusActionLoading}
          >
            {t("profilePage.absences.statusModal.reject")}
          </Button>
          <Button
            color="primary"
            radius="md"
            onClick={handleApprove}
            loading={isApproveLoading}
            disabled={isStatusActionLoading}
          >
            {t("profilePage.absences.statusModal.approve")}
          </Button>
        </Group>
      </Modal>
    </LayoutStandard>
  );
};

export default ProfilePage;
